/**
 * Trade terms of a deal and their duty & tax estimate
 * (`/tradeCosts/deals/<id>/terms/` and `/estimate/`).
 *
 * Both companies of the deal and its moderators can read them; moderators
 * edit them (`canEdit`). Until a moderator saves, the suggested defaults from
 * the deal are shown: supplier's country → buyer's country, and the matched
 * product's HS code (an empty HS code keeps following that product).
 */
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { formatDateTime } from "../../i18n/format";
import { getApiErrorMessage } from "../../lib/apiError";
import type {
  DealTradeTerms,
  DealTradeTermsSaveRequest,
  EstimateResponse,
} from "../../services/tradecosts/generated/model";
import {
  getTradeCostsDealsEstimateRetrieveQueryKey,
  getTradeCostsDealsTermsRetrieveQueryKey,
  useTradeCostsDealsEstimateRetrieve,
  useTradeCostsDealsTermsRetrieve,
  useTradeCostsDealsTermsUpdate,
} from "../../services/tradecosts/generated/trade-costs/trade-costs";
import TradeEstimateForm, {
  EMPTY_ESTIMATE_VALUES,
  type TradeEstimateValues,
  toEstimateRequest,
} from "./TradeEstimateForm";
import TradeEstimateResult from "./TradeEstimateResult";
import "./TradeEstimate.css";

function termsToValues(terms: DealTradeTerms): TradeEstimateValues {
  const s = terms.suggested;
  return {
    ...EMPTY_ESTIMATE_VALUES,
    originCountry: terms.originCountry || s.originCountry || EMPTY_ESTIMATE_VALUES.originCountry,
    destinationCountry: terms.destinationCountry || s.destinationCountry || EMPTY_ESTIMATE_VALUES.destinationCountry,
    destinationSubdivision: terms.destinationSubdivision ?? "",
    hsCode: terms.hsCode || s.hsCode || "",
    customsValue: terms.customsValue ?? "",
    currency: terms.currency || EMPTY_ESTIMATE_VALUES.currency,
    incoterm: terms.incoterm ?? "",
    freight: terms.freight ?? "",
    insurance: terms.insurance ?? "",
    importerVatRegistered:
      terms.importerVatRegistered === null || terms.importerVatRegistered === undefined
        ? ""
        : terms.importerVatRegistered
          ? "yes"
          : "no",
    quantity: terms.quantity ?? "",
    unit: terms.unit ?? "",
  };
}

const DealTradeTermsCard = ({ dealId, canEdit }: { dealId: number; canEdit: boolean }) => {
  const { t } = useTranslation(["trade", "common"]);
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const termsQuery = useTradeCostsDealsTermsRetrieve(dealId, { query: { retry: false } });
  const terms = termsQuery.data as DealTradeTerms | undefined;
  const estimateQuery = useTradeCostsDealsEstimateRetrieve(dealId, {
    query: { enabled: Boolean(terms?.isSet), retry: false },
  });
  const estimate = estimateQuery.data as EstimateResponse | undefined;

  const save = useTradeCostsDealsTermsUpdate({
    mutation: {
      onSuccess: () => {
        setEditing(false);
        qc.invalidateQueries({ queryKey: getTradeCostsDealsTermsRetrieveQueryKey(dealId) });
        qc.invalidateQueries({ queryKey: getTradeCostsDealsEstimateRetrieveQueryKey(dealId) });
      },
    },
  });

  if (termsQuery.isLoading) return null;
  if (termsQuery.error || !terms) {
    return (
      <section className="te-card">
        <h2 className="te-card__title">{t("deal.title")}</h2>
        <p className="te-error">{getApiErrorMessage(termsQuery.error, t("deal.loadError"))}</p>
      </section>
    );
  }

  const values = termsToValues(terms);

  return (
    <section className="te-card">
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h2 className="te-card__title">{t("deal.title")}</h2>
          <p className="te-help">
            {terms.isSet
              ? t("deal.savedAt", { date: terms.updatedAt ? formatDateTime(terms.updatedAt) : "" })
              : canEdit
                ? t("deal.notSetModerator")
                : t("deal.notSetCompany")}
          </p>
        </div>
        {canEdit && !editing ? (
          <div>
            <button type="button" className="te-btn" onClick={() => setEditing(true)}>
              {terms.isSet ? t("deal.edit") : t("deal.set")}
            </button>
          </div>
        ) : null}
      </div>

      {!editing ? (
        <dl className="te-form__grid" style={{ margin: "0 0 12px" }}>
          <Term label={t("form.origin")} value={values.originCountry} />
          <Term
            label={t("form.destination")}
            value={[values.destinationCountry, values.destinationSubdivision].filter(Boolean).join(" · ")}
          />
          <Term
            label={t("form.hsCode")}
            value={values.hsCode}
            hint={!terms.hsCode && terms.suggested.hsCode ? t("deal.fromProduct") : ""}
          />
          <Term
            label={t("form.value")}
            value={values.customsValue ? `${values.customsValue} ${values.currency}` : ""}
          />
          <Term label={t("form.incoterm")} value={values.incoterm} />
          {terms.note ? <Term label={t("deal.note")} value={terms.note} /> : null}
        </dl>
      ) : (
        <TradeEstimateForm
          initial={values}
          requireValue={false}
          submitLabel={t("deal.save")}
          busy={save.isPending}
          onSubmit={(v) => {
            const req = toEstimateRequest(v);
            save.mutate({
              id: dealId,
              data: {
                ...req,
                customsValue: req.customsValue || null,
                // Blank clears it (the API refuses null; the generated type omits blank).
                incoterm: (req.incoterm ?? "") as DealTradeTermsSaveRequest["incoterm"],
                note: note ?? terms.note ?? "",
              },
            });
          }}
          extra={
            <label>
              {t("deal.note")}
              <textarea
                className="te-input"
                rows={2}
                value={note ?? terms.note ?? ""}
                onChange={(e) => setNote(e.target.value)}
              />
            </label>
          }
        />
      )}
      {save.isError ? <p className="te-error">{getApiErrorMessage(save.error, t("deal.saveError"))}</p> : null}
      {editing ? (
        <button
          type="button"
          className="te-btn te-btn--ghost"
          style={{ marginTop: 8 }}
          onClick={() => setEditing(false)}
        >
          {t("common:actions.cancel")}
        </button>
      ) : null}

      {terms.isSet && estimateQuery.isError ? (
        <p className="te-help">{getApiErrorMessage(estimateQuery.error, t("deal.estimateError"))}</p>
      ) : null}
      {estimate && !editing ? (
        <div style={{ marginTop: 12 }}>
          <TradeEstimateResult estimate={estimate} />
        </div>
      ) : null}
    </section>
  );
};

const Term = ({ label, value, hint }: { label: string; value: string; hint?: string }) => {
  const { t } = useTranslation("trade");
  return (
    <div>
      <dt className="te-stat__label">{label}</dt>
      <dd style={{ margin: 0 }}>
        {value || t("deal.unset")}
        {hint ? <span className="te-field-hint"> {hint}</span> : null}
      </dd>
    </div>
  );
};

export default DealTradeTermsCard;
